import { Button, Dialog, DialogContent } from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { useEffect, useState } from "react";
import styles from "./styles.module.css";
import {
  buscarVersoesdaPrevia,
  criarVersaoPreviaComMotor,
} from "../../../services/api-planejamento-tributario.service";
import SnackBar from "../../SnackBar";
import Loading from "../../Loading";
import ModalAnalisarIcms from "../../../pages/PreviaICMS/ModalAnalisar";
import ModalNovaVersao from "../ModalNovaVersao";

const ModalVersoes = ({
  open,
  onClose,
  idPrevia,
  tipoPrevia = "PIS-COFINS", // "PIS-COFINS" ou "ICMS"
  contextHook, // Hook do contexto (usePreviaPisCofins ou usePreviaIcms)
  onReopenModal, // Função para reabrir o modal após criar versão
  dadosEmpresa = null, // Dados da empresa (razaoSocial, cnpj, etc.)
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [modalNovaVersao, setModalNovaVersao] = useState(false);
  const [openModalAnalisar, setOpenModalAnalisar] = useState(false);
  const getIdVersao = (v) => v?.idPreviaVersao ?? v?.idProjetoVersao ?? 0;
  const [versoes, setVersoes] = useState([
    {
      idPreviaVersao: 0,
      descricao: "Original",
      dtCadastro: null,
      versao: null,
    },
  ]);
  const { setVersaoSelecionada, setDadosVersao } = contextHook();

  // ✅ NOVA LÓGICA ELEGANTE: Função global para fechar modal e reabrir com loading
  useEffect(() => {
    window.reabrirModalVersoes = async () => {
      // ✅ FECHAR MODAL COMPLETAMENTE
      setModalNovaVersao(false);
      onClose(); // Fecha o modal de versões

      // ✅ Comportamento automático removido para evitar conflitos
      // O modal de versões será reaberto manualmente quando necessário
    };

    return () => {
      delete window.reabrirModalVersoes;
    };
  }, [idPrevia, tipoPrevia, dadosEmpresa, onClose]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await buscarVersoesdaPrevia(tipoPrevia, idPrevia);
        const data = response.versoes || response || [];

        // Manter a versão "Original" e adicionar as versões da API
        const versaoOriginal = {
          idPreviaVersao: 0,
          descricao: "Original",
          dtCadastro: null,
          versao: null,
        };
        const versoesFinais = [versaoOriginal, ...data];
        setVersoes(versoesFinais);
      } catch (error) {
        setObjSnackBar({
          type: "error",
          msg: "Erro ao carregar versões da prévia.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (open && idPrevia) {
      fetchData();
    }
  }, [open, idPrevia, tipoPrevia]);

  useEffect(() => {
    if (!open) return;

    // Reset SnackBar quando modal abre
    setObjSnackBar({ type: "", msg: "" });
  }, [open]);

  // Função para recarregar versões
  const recarregarVersoes = async () => {
    try {
      const response = await buscarVersoesdaPrevia(tipoPrevia, idPrevia);
      const versoesData = response.versoes || response || [];
      const versoesComOriginal = [
        { idPreviaVersao: 0, descricao: "Original" },
        ...versoesData,
      ];
      setVersoes(versoesComOriginal);
    } catch (error) {}
  };

  const handleClose = () => {
    // Reset estados internos
    setModalNovaVersao(false);
    setVersoes([{ idPreviaVersao: 0, descricao: "Original" }]);
    setOpenModalAnalisar(false);
    setObjSnackBar({ type: "", msg: "" }); // Reset SnackBar

    // Força o fechamento do modal
    if (typeof onClose === "function") {
      try {
        onClose();
      } catch (error) {}
    } else {
    }

    // Força o fechamento visual do modal
    setTimeout(() => {
      // Força o fechamento visual
      if (typeof onClose === "function") {
        onClose();
      }
    }, 100);
  };

  const handleCreate = async (nomeVersao, categoria) => {
    setLoadingCreate(true);

    try {
      // Para PIS/COFINS e ICMS, usar o service com motor
      const resultado = await criarVersaoPreviaComMotor({
        tipoPrevia,
        descricao: nomeVersao,
        idPrevia: idPrevia,
        idCategoria: categoria.idCategoriaTributaria, // Usar ID da categoria selecionada
      });

      // Fechar modal de nova versão
      setModalNovaVersao(false);

      // Verificar resposta da API
      if (resultado && resultado.sucesso) {
        // Mostrar snackbar de sucesso
        const snackbarData = {
          type: "success",
          msg: resultado.mensagem || "Versão criada com sucesso!",
        };
        setObjSnackBar(snackbarData);

        // Adicionar nova versão ao estado local
        if (resultado.versao) {
          const novaVersao = {
            idPreviaVersao: resultado.versao.idPreviaVersao,
            descricao: resultado.versao.descricao || nomeVersao,
            dtCadastro:
              resultado.versao.dataCriacao ||
              resultado.versao.dtCadastro ||
              new Date().toISOString(),
            // Adicionar outros campos se necessário
            ...resultado.versao, // Incluir todos os campos da resposta da API
          };

          setVersoes((prevVersoes) => {
            const versoesComOriginal = [
              { idPreviaVersao: 0, descricao: "Original" },
              ...prevVersoes.filter((v) => v.idPreviaVersao !== 0), // Remove "Original" duplicado
              novaVersao,
            ];
            return versoesComOriginal;
          });
        }
      } else {
        setObjSnackBar({
          type: "error",
          msg: resultado?.mensagem || "Erro ao criar a versão",
        });
      }
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg: error.response?.data?.mensagem || "Ops! Erro ao criar.",
      });
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleAnalisar = (versao) => {
    // Verificar se os dados necessários estão presentes
    if (!versao || !versao.idPreviaVersao) {
      return;
    }

    if (!idPrevia) {
      console.error(
        "❌ DEBUG - handleAnalisar - idPrevia não disponível:",
        idPrevia
      );
      return;
    }

    setVersaoSelecionada(versao);
    setDadosVersao(null);
    setOpenModalAnalisar(true);
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        {loading ? (
          <Loading loading={loading} />
        ) : (
          <DialogContent>
            <div className={styles.containerTitle}>
              <div className={styles.title}>
                <FormatListNumberedIcon /> Versões da Prévia
              </div>

              <Button
                variant="contained"
                onClick={() => setModalNovaVersao(true)}
                className={styles.btnAnalisar}
                size="small"
              >
                Adicionar Versão
              </Button>
            </div>
            <div className={styles.containerVersao}>
              {(Array.isArray(versoes) ? versoes : []).map((versao, index) => {
                return (
                  <div key={index} className={styles.itemVersao}>
                    <span>
                      <h4>{versao.descricao}</h4>
                      <p style={{ fontSize: ".8rem", color: "#A4A4A4" }}>
                        {versao.dtCadastro
                          ? new Date(versao.dtCadastro).toLocaleDateString(
                              "pt-BR"
                            )
                          : "-"}
                      </p>
                    </span>
                    {getIdVersao(versao) !== 0 && (
                      <Button
                        variant="outlined"
                        onClick={() => handleAnalisar(versao)}
                        className={styles.btnFechar}
                        size="small"
                      >
                        Analisar
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className={styles.containerBtn}>
              <Button
                variant="outlined"
                onClick={handleClose}
                className={styles.btnFechar}
              >
                Fechar
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <ModalNovaVersao
        open={modalNovaVersao}
        onClose={() => setModalNovaVersao(false)}
        onCreate={handleCreate}
        loading={loadingCreate}
        tipoPrevia={tipoPrevia}
        idPrevia={idPrevia}
        dadosPrevia={{
          razaoSocial: dadosEmpresa?.razaoSocial || "Empresa",
        }}
        versoesExistentes={versoes}
        isProjeto={false}
      />

      {/* SnackBar tradicional */}
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />

      {/* Modal de Análise Dinâmico */}
      {tipoPrevia === "ICMS" && (
        <ModalAnalisarIcms
          open={openModalAnalisar}
          setOpen={setOpenModalAnalisar}
          isTab={false}
        />
      )}
    </>
  );
};

export default ModalVersoes;
